/**
 * AWS Amplify configuration for Cognito authentication.
 *
 * Reads Cognito User Pool settings from environment variables.
 * These are injected at build time via Vite's import.meta.env.
 */

export interface AmplifyConfig {
  Auth: {
    Cognito: {
      userPoolId: string;
      userPoolClientId: string;
      loginWith?: {
        oauth?: {
          domain: string;
          scopes: string[];
          redirectSignIn: string[];
          redirectSignOut: string[];
          responseType: 'code' | 'token';
          providers: Array<{ custom: string } | 'Google' | 'Apple'>;
        };
      };
    };
  };
}

/**
 * Returns the Amplify configuration object using environment variables.
 *
 * Expected environment variables (set via .env or build config):
 * - VITE_COGNITO_USER_POOL_ID
 * - VITE_COGNITO_CLIENT_ID
 * - VITE_COGNITO_DOMAIN (optional, for social login)
 * - VITE_AUTH_REDIRECT_SIGN_IN (optional)
 * - VITE_AUTH_REDIRECT_SIGN_OUT (optional)
 */
export function getAmplifyConfig(): AmplifyConfig {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '';
  const userPoolClientId = import.meta.env.VITE_COGNITO_CLIENT_ID ?? '';
  const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN ?? '';
  const redirectSignIn = import.meta.env.VITE_AUTH_REDIRECT_SIGN_IN ?? 'http://localhost:5173/';
  const redirectSignOut = import.meta.env.VITE_AUTH_REDIRECT_SIGN_OUT ?? 'http://localhost:5173/login';

  const config: AmplifyConfig = {
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
      },
    },
  };

  // Add OAuth configuration if domain is provided (for social login)
  if (cognitoDomain) {
    config.Auth.Cognito.loginWith = {
      oauth: {
        domain: cognitoDomain,
        scopes: ['email', 'openid', 'profile'],
        redirectSignIn: [redirectSignIn],
        redirectSignOut: [redirectSignOut],
        responseType: 'code',
        providers: ['Google', 'Apple'],
      },
    };
  }

  return config;
}
